import { useEffect, useRef, useState } from 'react';
import { IconChevronLeft, IconChevronRight, IconImageOff, IconX } from '../icons';
import { useTranslate } from '../i18n/react';
import { Button, IconButton } from './Button';

export interface ViewableMedia {
  id: string;
  url: string | null | undefined;
  contentType: string | null;
}

/**
 * One attachment, filling the screen.
 *
 * <p>The thumbnails are eighty-four pixels square, which is enough to tell one photograph
 * from another and not enough to see what is wrong with a shelf. The auditor took the
 * picture to show somebody something; this is where they look at it.</p>
 *
 * <p>It steps between the attachments of the same question rather than closing and
 * reopening. A question that demanded a photograph usually has three, and they are three
 * views of one thing.</p>
 */
export function MediaViewer({
  items,
  startAt,
  label,
  onRefresh,
  onClose,
}: {
  items: ViewableMedia[];
  startAt: number;
  /** What this is of — the question's code or text, for a reader who cannot see it. */
  label: string;

  /**
   * Fetches the audit again, which is what hands out fresh links.
   *
   * <p>The links are signed and last fifteen minutes. A thumbnail refreshes itself quietly;
   * this does not, because the auditor is looking straight at it and a silent retry that
   * also fails leaves them staring at nothing. Offered as a button instead, after the first
   * failure.</p>
   *
   * <p>Given the file's id, so that the screen asks for a fresh address for this one.</p>
   */
  onRefresh: (id: string) => void;
  onClose: () => void;
}) {
  const t = useTranslate();
  const [at, setAt] = useState(startAt);
  const [broken, setBroken] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);

  const item = items[at];
  const many = items.length > 1;

  // A new address is a new chance. Without this the "could not be opened" stayed up after
  // the fresh link it asked for had arrived, because nothing told it the link had changed.
  const url = item?.url;
  useEffect(() => setBroken(false), [url]);

  const step = (by: number) => {
    setBroken(false);
    setAt((n) => (n + by + items.length) % items.length);
  };

  useEffect(() => {
    returnTo.current = document.activeElement as HTMLElement;
    panel.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      // Through the same door as the buttons, so a failure on one does not follow the
      // auditor onto the next.
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };

    document.addEventListener('keydown', onKey);

    // Nothing behind this should scroll while it is open: on a phone a swipe meant for the
    // photograph otherwise moves the form underneath it.
    const had = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = had;
      returnTo.current?.focus();
    };
  }, [items.length, onClose]);

  if (!item) return null;

  const isVideo = item.contentType?.startsWith('video/') ?? false;

  return (
    <div
      className="nv-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      ref={panel}
      tabIndex={-1}
      // The backdrop closes it; the picture does not. A tap that lands on the photograph
      // while reaching for it would otherwise put it away.
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="nv-viewer__bar">
        {many ? (
          <span className="nv-viewer__count">{`${at + 1} / ${items.length}`}</span>
        ) : (
          <span />
        )}
        <IconButton label={t('common.close')} icon={IconX} onClick={onClose} />
      </div>

      <div className="nv-viewer__stage" onClick={(e) => e.target === e.currentTarget && onClose()}>
        {!item.url ? (
          <p className="nv-viewer__missing">
            <IconImageOff size={32} /> {t('audit.mediaPending')}
          </p>
        ) : broken ? (
          <div className="nv-viewer__missing">
            <IconImageOff size={32} />
            <span>{t('audit.mediaExpired')}</span>
            <Button variant="secondary" onClick={() => onRefresh(item.id)}>{t('common.retry')}</Button>
          </div>
        ) : isVideo ? (
          // Keyed on the id, so stepping to the next one loads it rather than leaving the
          // previous frame under a new source.
          <video
            key={item.id}
            className="nv-viewer__media"
            src={item.url}
            controls
            autoPlay
            playsInline
            onError={() => setBroken(true)}
          />
        ) : (
          <img
            key={item.id}
            className="nv-viewer__media"
            src={item.url}
            alt={label}
            onError={() => setBroken(true)}
          />
        )}
      </div>

      {many ? (
        <div className="nv-viewer__steps">
          <IconButton label={t('common.previous')} icon={IconChevronLeft} onClick={() => step(-1)} />
          <IconButton label={t('common.next')} icon={IconChevronRight} onClick={() => step(1)} />
        </div>
      ) : null}
    </div>
  );
}
